import { Component, ViewChild, inject } from '@angular/core';
import { environment } from 'src/environments/environment';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { ButtonGroupModule } from 'primeng/buttongroup';
import { CalendarModule } from 'primeng/calendar';
import { MenuModule } from 'primeng/menu';
import { SelectButtonModule } from 'primeng/selectbutton';
import { SplitButtonModule } from 'primeng/splitbutton';
import { TooltipModule } from 'primeng/tooltip';
import { CrumbDatePickerComponent } from './crumb-date-picker.component';
import {
    ScheduleControlsService,
    ControlsGranularity,
} from './schedule-controls.service';
import { DailySummaryComponent } from '../daily-summary/daily-summary.component';
import { dayjs } from '@karve.it/core';

/**
 * Shared schedule-page date/controls bar rendered above the schedule tabs
 * (in schedule-page.component). One implementation of the controls every
 * schedule tab used to re-implement: date selector, Today, < >, gear, pencil
 * (day view only), refresh — plus Day/Week/Month presets for tabs that
 * support multiple granularities.
 *
 * Tabs opt in by registering a ScheduleControlsConfig with
 * ScheduleControlsService; the bar picks up mode, menu items and handlers
 * from there.
 */
@Component({
    selector: 'app-schedule-controls-bar',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        ButtonModule,
        ButtonGroupModule,
        CalendarModule,
        CrumbDatePickerComponent,
        DailySummaryComponent,
        MenuModule,
        SelectButtonModule,
        SplitButtonModule,
        TooltipModule,
    ],
    templateUrl: './schedule-controls-bar.component.html',
    styleUrls: ['./schedule-controls-bar.component.scss'],
})
export class ScheduleControlsBarComponent {
    protected readonly controls = inject(ScheduleControlsService);
    protected readonly route = inject(ActivatedRoute);

    @ViewChild('gearMenu') gearMenu: any;

    /* Granularity presets are rendered by the segmented p-selectButton as
       tab-like chips; the label/value pairs stay capitalized there. */

    protected get config() { return this.controls.config(); }
    protected get isRangeMode() { return this.config?.mode === 'range'; }
    protected get granularity() { return this.controls.granularity(); }
    get granularityModel() {
        return this.controls.granularity();
    }
    set granularityModel(value: ControlsGranularity) {
        if (value) {
            this.controls.setGranularity(value);
        }
    }
    protected get showGear() { return (this.config?.gearMenu ?? []).length > 0 || !!this.config?.onGear; }
    protected get showRefresh() { return !!this.config?.onRefresh; }
    protected get showPencil() { return !!(this.config?.canEdit && this.config?.onEdit); }

    // --- calendar models (get/set mirrors onto the service signals) ---

    /** Day-mode single date. */
    get dayDate(): Date {
        return this.controls.date();
    }
    set dayDate(value: Date) {
        if (value) {
            this.controls.setDate(value);
        }
    }

    /** Range-mode [start, end] for the PrimeNG selectionMode="range" picker.
     *  While only one end is picked (length 1) the stored window is left
     *  alone so the range isn't half-applied. The reference is memoized: a
     *  fresh array per read plus two-way binding loops change detection
     *  forever (page freeze). */
    get rangeDates(): Date[] {
        const start = this.controls.date();
        const end = this.controls.dateTo() ?? start;
        const startMs = start?.getTime();
        const endMs = end?.getTime();
        if (!this.rangeDatesCache
            || this.rangeDatesCacheStartMs !== startMs
            || this.rangeDatesCacheEndMs !== endMs) {
            this.rangeDatesCache = [start, end];
            this.rangeDatesCacheStartMs = startMs;
            this.rangeDatesCacheEndMs = endMs;
        }
        return this.rangeDatesCache;
    }
        set rangeDates(value: Date[]) {
        // PrimeNG emits [start, null] for the first end of a range pick —
        // length is 2 but the end is missing; applying it would clear the
        // window's end (and possibly trigger a reload) mid-pick (CodeRabbit).
        if (value?.length === 2 && value[0] && value[1]) {
            this.controls.setDate(value[0]);
            this.controls.setDateTo(value[1]);
        }
    }
    private rangeDatesCache: Date[] | null = null;
    private rangeDatesCacheStartMs?: number;
    private rangeDatesCacheEndMs?: number;

    ngOnInit(): void {
        // Bootstrap the controls' date window from the URL on entry (&date=
        // and optional &dateTo=, the schedule tabs' shared params; dispatch
        // and the day view persist ISO). Defaults to today.
        //
        // The service is root-scoped: with NO date params in the URL (e.g. a
        // recreated bar after an in-app re-navigation) keep the active
        // window; a bare &date= starts a NEW window (clear any retained
        // dateTo first so it cannot pair an old end with the new start).
        const routeDate = this.route.snapshot.queryParamMap.get('date');
        const routeDateTo = this.route.snapshot.queryParamMap.get('dateTo');
        const hasUrlParams = routeDate !== null || routeDateTo !== null;
        const parsedDate = routeDate && dayjs(routeDate).isValid() ? dayjs(routeDate) : null;
        const parsedDateTo = routeDateTo && dayjs(routeDateTo).isValid() ? dayjs(routeDateTo) : null;
        // The week view's record window is the next 7 days from the anchor
        // (Ray): a deep link carrying only &date= still opens the full span.
        const isWeekPath = /\/schedule\/week(\/|$)/.test(window.location.pathname);

        if (parsedDate && !parsedDateTo && !isWeekPath) {
            this.controls.setDateTo(null);
        }
        if (parsedDate) {
            this.controls.setDate(parsedDate.toDate());
        } else if (hasUrlParams) {
            this.controls.setDate(new Date());
        } else if (!this.controls.date()) {
            // Neither the URL nor the shared service holds a date: seed from
            // the SAVED schedule date so the bar agrees with the calendar,
            // which loads that saved date (CodeRabbit). Today is the last resort.
            const savedScheduleDate = sessionStorage.getItem(environment.lskeys.scheduleDate);
            // Today fallback = the business calendar day, same as goToday()'s
            // anchor (CodeRabbit). The picker still gets a local Date.
            const seed = savedScheduleDate && dayjs(savedScheduleDate).isValid()
                ? dayjs(savedScheduleDate)
                : this.controls.businessToday();
            this.controls.setDate(seed.toDate());
        }
        if (parsedDateTo) {
            this.controls.setDateTo(parsedDateTo.toDate());
        } else if (parsedDate && isWeekPath) {
            // Fresh date window on the week tab = the whole displayed week
            // (Mon–Sun, FC firstDay 1 — the grid's own window; the datesSet
            // sync keeps it authoritative). setDate first so a day-mode
            // registration cannot clear the end after it is written.
            const weekStart = parsedDate.subtract((parsedDate.day() + 6) % 7, 'day').startOf('day');
            this.controls.setDate(weekStart.toDate());
            this.controls.setDateTo(weekStart.add(6, 'day').toDate());
        } else if (!parsedDate && hasUrlParams) {
            this.controls.setDateTo(null);
        }
    }

    setGranularity(g: ControlsGranularity): void {
        this.controls.setGranularity(g);
    }

    goToday(): void {
        this.controls.goToday();
    }

    step(dir: -1 | 1): void {
        this.controls.step(dir);
    }

    /** Gear: either the bar's own p-menu popup (tab provided MenuItems) or
     *  the tab's custom handler (e.g. dispatch's overlay panel, which needs
     *  the click event as its anchor). */
    toggleGear(event: MouseEvent): void {
        if (this.config?.onGear) {
            this.config.onGear(event);
        } else if (this.gearMenu) {
            this.gearMenu.toggle(event);
        }
    }

    runEdit(): void {
        this.config?.onEdit?.();
    }

    runFilters(event: MouseEvent): void {
        this.config?.onFilters?.(event);
    }

    /** The summary strip's event-type badge click (the day view's
     *  filter-by-type affordance the card's strip used to handle). */
    runSummaryFilter(type: string): void {
        this.controls.summary()?.onFilterByType?.(type);
    }

    runRefresh(): void {
        const onRefresh = this.config?.onRefresh;
        if (!onRefresh) { return; }
        this.controls.runRefresh(onRefresh);
    }
}